import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

// Universal lightbox for YouTube + self-hosted videos.
// - YouTube videos render via privacy-enhanced nocookie embed
// - Self-hosted (.mp4) videos render via the native <video> element
// - ESC + click-outside dismiss
// - Body scroll locked while open
export default function VideoLightbox({ video, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!video) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Focus the close button for screen readers
    requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [video, onClose]);

  if (!video) return null;

  const isPortrait = video.orientation === "portrait";
  const isYouTube = !!video.youtube_id;
  const ytSrc = isYouTube
    ? `https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&modestbranding=1&rel=0&playsinline=1`
    : null;
  const ytCanonical = isYouTube
    ? `https://www.youtube.com/watch?v=${video.youtube_id}`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      style={{ background: "rgba(7,10,13,0.92)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="video-lightbox"
    >
      <button
        ref={dialogRef}
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 inline-flex items-center justify-center transition-opacity hover:opacity-80"
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          background: "rgba(255,255,255,0.10)",
          color: "#F5F7FA",
          border: "1px solid rgba(255,255,255,0.18)",
        }}
        aria-label="Close video"
        data-testid="video-lightbox-close"
      >
        <X size={20} aria-hidden />
      </button>

      <div
        className="relative w-full"
        style={{
          maxWidth: isPortrait ? "min(420px, 95vw)" : "min(1180px, 95vw)",
          aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
          maxHeight: "90vh",
        }}
      >
        {isYouTube ? (
          <iframe
            src={ytSrc}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full"
            style={{ border: 0, borderRadius: 6, background: "#000" }}
            data-testid="video-lightbox-iframe"
          />
        ) : (
          <video
            src={video.mp4}
            poster={video.poster}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain"
            style={{ borderRadius: 6, background: "#000" }}
            data-testid="video-lightbox-player"
          />
        )}

        {(video.title || video.filmed_at || ytCanonical) && (
          <div
            className="absolute left-0 right-0 -bottom-16 md:-bottom-20 px-1 flex items-baseline justify-between gap-4 text-white"
          >
            <div className="min-w-0">
              <div className="font-display text-[1rem] md:text-[1.05rem] truncate" style={{ color: "#F5F7FA" }}>
                {video.title}
              </div>
              {video.filmed_at && (
                <div className="text-[0.78rem] font-light mt-0.5" style={{ color: "rgba(245,247,250,0.65)" }}>
                  {video.filmed_at}
                </div>
              )}
            </div>
            {ytCanonical && (
              <a
                href={ytCanonical}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-[0.78rem] font-mono uppercase hover:text-[#1FB6C1] transition-colors"
                style={{ color: "rgba(245,247,250,0.85)", letterSpacing: "0.16em" }}
                data-testid="video-lightbox-yt-fallback"
              >
                Watch on YouTube ↗
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
